import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getPostMeta, getPostBody, blogPosts } from './blog/posts';
import { FloatingFoodEmojis } from './blog/BlogVisuals';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const meta = slug ? getPostMeta(slug) : undefined;
  const relatedPosts = blogPosts.filter(post => post.slug !== slug).slice(0, 2);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (meta) {
      document.title = `${meta.title} | IntelliEats Blog`;
      let description = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (!description) {
        description = document.createElement('meta');
        description.name = 'description';
        document.head.appendChild(description);
      }
      description.content = meta.excerpt;
    }
    // Google AdSense - inject into <head>
    if (!document.querySelector('script[src*="adsbygoogle"]')) {
      const adsScript = document.createElement('script');
      adsScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3041826185319356';
      adsScript.async = true;
      adsScript.setAttribute('crossorigin', 'anonymous');
      document.head.appendChild(adsScript);
    }
  }, [meta]);
  if (!meta) {
    return <Navigate to="/blog" replace data-id="element-2013" data-mp="3kvuhryz3pls6" />;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-2014" data-mp="3oyr686bboc41">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2015" data-mp="2jxz4e9x4bo6">
        {/* Header */}
        <header className="mb-12 text-center" data-id="element-2016" data-mp="2lsn61ghkjwh">
          <Link to="/blog" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-2017" data-mp="318wu5qyturku">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2018" data-mp="de12yx9rmj0c" />
            Back to Blog
          </Link>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-2019" data-mp="8fpsckbnc4h2">
            {meta.title}
          </h1>
          <div className="flex items-center justify-center text-sm text-slate-500 space-x-4" data-id="element-2020" data-mp="m6cuynkj2eif">
            <span data-id="element-2021" data-mp="i5sx1hs4r6v8">By IntelliEats Team</span>
            <span data-id="element-2022" data-mp="3ql2w0h0q3vys">•</span>
            <span className="flex items-center" data-id="element-2023" data-mp="3qkayv1rh9q6e">
              <Clock className="w-4 h-4 mr-1" data-id="element-2024" data-mp="3qn6wsq3bseuu" />
              {meta.readTime}
            </span>
          </div>
        </header>

        <FloatingFoodEmojis category={meta.category} data-id="element-2025" data-mp="3g4vqgjy887i" />

        <div className="mb-10 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm italic leading-relaxed text-slate-600" data-id="element-2026" data-mp="3hy01p73agcc">
          This guide is for educational purposes and does not replace medical or dietary advice. Food additives are evaluated individually, and ingredients approved by food-safety authorities are permitted for specific uses and levels. If you have allergies, medical conditions, or questions about your diet, consult a qualified healthcare professional.
        </div>

        {/* Body */}
        {getPostBody(meta.slug)}

        {/* Medical Disclaimer */}
        <div className="mt-16 bg-slate-50 border border-slate-200 rounded-2xl p-6" data-id="element-2027" data-mp="18gl555nmnxgi">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2" data-id="element-2028" data-mp="1dffn65drthzk">
            Medical Disclaimer
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed m-0" data-id="element-2029" data-mp="2161ws13fbabo">
            The information in this article is provided for educational purposes
            only and is not intended as medical, nutritional, or dietary advice.
            Ingredients approved by regulatory authorities are considered safe
            when used within established limits. Always consult a qualified
            healthcare professional or registered dietitian for guidance
            tailored to your personal health needs. See our{' '}
            <Link to="/editorial-policy" className="text-brand-600 hover:text-brand-700 font-medium" data-id="element-2030" data-mp="107qhpkuqe1wp">
              Editorial Policy
            </Link>{' '}
            to learn how we research and review our content.
          </p>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && <div className="mt-16 pt-12 border-t border-slate-200" data-id="element-2031" data-mp="m9ijl9lykujy">
            <h2 className="text-2xl font-black text-slate-900 mb-8 text-center" data-id="element-2032" data-mp="1jvzcm2nnslzw">
              Keep Reading
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-2033" data-mp="1ex4w57d5z9iy">
              {relatedPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group block bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left" data-id="element-2034" data-mp="1a4brejhp416v">
                  <div className="relative h-40 overflow-hidden" data-id="element-2035" data-mp="1xu63uzy3rnql">
                    <img src={post.image} alt={post.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2036" data-mp="257ou3b3xb8wq" />
                    <span aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 select-none rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-black tracking-tight backdrop-blur-sm" data-id="element-2037" data-mp="2f6w2rkzpqfdi">
                      <span className="text-slate-900/70" data-id="element-2038" data-mp="2t2zv18r10dnc">Intelli</span>
                      <span className="text-brand-600/80" data-id="element-2039" data-mp="2xv14yo0q1sa7">Eats</span>
                    </span>
                  </div>
                  <div className="p-6" data-id="element-2040" data-mp="32t2ofa227tm7">
                    <span className="px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-bold tracking-wider uppercase" data-id="element-2041" data-mp="32w8bykv1inft">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-slate-900 mt-3 mb-3 leading-snug group-hover:text-brand-700 transition-colors line-clamp-2" data-id="element-2042" data-mp="3bspnhp7bgnc3">
                      {post.title}
                    </h3>
                    <span className="inline-flex items-center text-sm font-semibold text-brand-600 group-hover:text-brand-700" data-id="element-2043" data-mp="1lim7thy3vebi">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2044" data-mp="1gqmck2szd9l9" />
                    </span>
                  </div>
                </Link>)}
            </div>
          </div>}
      </article>
    </motion.div>;
}